import { useLazyQuery } from '@apollo/client/react';
import { Badge } from '@optools/components/ui/badge';
import { Card } from '@optools/components/ui/card';
import { Separator } from '@optools/components/ui/separator';
import { BTC_GAME_PIPELINE } from '@optools/services/operator-gateway/btc/queries';
import { BtcSchedulePipeline } from '@optools/services/operator-gateway/btc/types';
import {
  MediaFeedType,
  PipelineStatus,
  SourceType,
  StreamState,
} from '@optools/services/operator-gateway/types/generated/graphql';
import { useBroadcastSourceUpdate } from '@optools/tools/btc-tools/hooks/useBroadcastSourceUpdate';
import { useProgramSourceSwitch } from '@optools/tools/btc-tools/hooks/useProgramSourceSwitch';
import { determineGameState, GAME_STATE } from '@optools/utils/game-state';
import { Cable } from 'lucide-react';
import { useEffect } from 'react';

import { BroadcastSourceSwitch } from './broadcast-source-switch';

const { ERROR, PROVISIONED, RUNNING, WARNING } = PipelineStatus;

export type ProgramSourceControlsProps = {
  pipeline: Pick<
    BtcSchedulePipeline,
    | 'id'
    | 'activeSlateCode'
    | 'gameSource'
    | 'postgameSource'
    | 'pregameSource'
    | 'programSource'
    | 'status'
    | 'streamState'
  >;
  gamePk: number;
  feedType: MediaFeedType;
  mediaId: string;
  date: string;
};

export function BroadcastSourceControls({
  date,
  feedType,
  gamePk,
  mediaId,
  pipeline,
}: ProgramSourceControlsProps) {
  const { gameSource, id, postgameSource, pregameSource, programSource, status, streamState } =
    pipeline;

  const [refreshPipelines, { called, loading: refreshPipelinesLoading, refetch }] = useLazyQuery(
    BTC_GAME_PIPELINE,
    {
      fetchPolicy: 'network-only',
    },
  );

  const { handleBroadcastSourceChange, isBroadcastSourcePending } = useBroadcastSourceUpdate({
    date,
    feedType,
    gamePk,
    pipelineId: id,
    programSource,
  });

  const { handleSwitchProgramSource, isProgramSourcePending } = useProgramSourceSwitch({
    date,
    gamePk,
    mediaId,
    pipelineId: id,
    programSource,
  });

  const disabled = ![RUNNING, WARNING, PROVISIONED, ERROR].includes(status);

  const gameState = determineGameState(pipeline);

  const showProgramSource =
    gameState === GAME_STATE.STAY_TUNED || streamState === StreamState.STREAM_OFF;

  useEffect(() => {
    void (called ? refetch() : refreshPipelines({ variables: { gamePk, scheduleDate: date } }));
  }, [called, date, gamePk, gameState, refetch, refreshPipelines]);

  const loading = isBroadcastSourcePending || isProgramSourcePending || refreshPipelinesLoading;

  return (
    <Card className="flex flex-col items-start gap-6 p-4">
      <div className="flex w-full items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="rounded border border-accent-foreground/70 p-1">
            <Cable className="size-4" />
          </div>
          <h2 className="font-bold">Broadcast Sources</h2>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-xs">Active Source:</span>
          <Badge>{programSource}</Badge>
        </div>
      </div>
      <div className="-ml-2 flex gap-4">
        {showProgramSource && (
          <>
            <BroadcastSourceSwitch
              active={true}
              broadcastSourceType="PROGRAM"
              broadcastSourceValue={programSource}
              disabled={disabled}
              label="Program Source"
              loading={loading}
              onProgramSourceChange={handleSwitchProgramSource}
            />
            <Separator className="h-full w-px bg-muted" orientation="vertical" />
          </>
        )}
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.PRE_GAME}
          broadcastSourceType={SourceType.PREGAME}
          broadcastSourceValue={pregameSource}
          disabled={disabled}
          label="Pre-Game"
          loading={loading}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.GAME}
          broadcastSourceType={SourceType.GAME}
          broadcastSourceValue={gameSource}
          disabled={disabled}
          label="Game"
          loading={loading}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
        <BroadcastSourceSwitch
          active={gameState === GAME_STATE.POST_GAME}
          broadcastSourceType={SourceType.POSTGAME}
          broadcastSourceValue={postgameSource}
          disabled={disabled}
          label="Post-Game"
          loading={loading}
          onBroadcastSourceChange={handleBroadcastSourceChange}
        />
      </div>
    </Card>
  );
}
